import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Paperclip, Calendar as CalIcon, Download, Users } from "lucide-react";

export function AdminFounderConcierge({ api }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [meetingAt, setMeetingAt] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try { setThreads((await api.get("/admin/concierge/threads")).data); }
    catch { toast.error("Could not load concierge threads."); }
    finally { setLoading(false); }
  }, [api]);

  const loadThread = useCallback(async (uid) => {
    try {
      const { data } = await api.get(`/admin/concierge/${uid}`);
      setThread(data);
    } catch { toast.error("Could not load thread."); }
  }, [api]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (selected) loadThread(selected); }, [selected, loadThread]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("body", text.trim() || "(attachment)");
      if (meetingAt) fd.append("meeting_at", meetingAt);
      if (meetingLink) fd.append("meeting_link", meetingLink);
      if (file) fd.append("attachment", file);
      await api.post(`/admin/concierge/${selected}`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null); setMeetingAt(""); setMeetingLink("");
      toast.success("Sent to the Founder.");
      loadThread(selected); loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    finally { setBusy(false); }
  };

  const downloadAttachment = async (uid, m) => {
    try {
      const r = await api.get(`/admin/concierge/${uid}/attachment/${m.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = m.attachment_filename || "vinci-concierge";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download."); }
  };

  return (
    <div data-testid="admin-founder-concierge">
      <div className="rounded-sm border-2 border-[#C9A961]/40 bg-gradient-to-br from-[#141008] via-[#0f0d0a] to-[#141008] p-6 mb-8 flex items-start gap-5">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-[#C9A961] text-black shrink-0">
          <MessageCircle size={22} />
        </span>
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
            · Super Master Command · Founder Concierge ·
          </div>
          <h3 className="font-serif text-2xl text-[#F5F5F0] mt-1">Private inbox with each Founder</h3>
          <p className="font-body text-sm text-[#C9C9C2] mt-2 leading-relaxed">
            Drop bespoke notes, documents and meeting invites. Founders reply
            from their Presidential Suite — every message is timestamped and
            archived.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        {/* Threads */}
        <div className="rounded-sm border border-[#C9A961]/20 bg-[#0f0d0a] p-4">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961] mb-3 flex items-center gap-2">
            <Users size={12} /> Founders
          </div>
          {loading ? <p className="font-body text-[#8A8A82] text-sm">Loading…</p>
            : threads.length === 0 ? <p className="font-body text-[#8A8A82] text-sm italic">No active founders.</p>
            : <div className="space-y-1">
                {threads.map((t) => (
                  <button key={t.founder_id} onClick={() => setSelected(t.founder_id)}
                    data-testid={`thread-${t.founder_id}`}
                    className={`w-full text-left p-3 rounded-sm transition-colors ${
                      selected === t.founder_id
                        ? "bg-[#C9A961]/[0.08] border border-[#C9A961]/50"
                        : "border border-transparent hover:bg-white/[0.03]"
                    }`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-sm text-[#F5F5F0]">{t.founder_name}</span>
                      {t.unread_from_founder > 0 && (
                        <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#C9A961] text-black text-[9px] font-bold flex items-center justify-center">
                          {t.unread_from_founder}
                        </span>
                      )}
                    </div>
                    <div className="font-body text-[10px] text-[#8A8A82] mt-0.5 truncate">
                      {t.business_name} · {t.shareholding_pct.toFixed(2)}%
                    </div>
                    {t.last_message_preview && (
                      <div className="font-body text-[11px] text-[#C9C9C2] mt-1 truncate italic">
                        {t.last_message_preview}
                      </div>
                    )}
                  </button>
                ))}
              </div>
          }
        </div>

        {/* Thread view */}
        <div className="rounded-sm border border-[#C9A961]/20 bg-[#0f0d0a] p-4">
          {!thread ? (
            <p className="font-body text-[#8A8A82] italic text-center py-16">
              Select a Founder from the left to open their private line.
            </p>
          ) : (
            <>
              <div className="pb-3 mb-3 border-b border-[#C9A961]/15">
                <div className="font-serif text-xl text-[#F5F5F0]">{thread.founder.name}</div>
                <div className="font-body text-xs text-[#8A8A82]">{thread.founder.email} · {thread.founder.business_name}</div>
              </div>
              <div ref={scrollRef} className="max-h-[380px] overflow-y-auto space-y-3 pr-2 mb-4">
                {thread.messages.length === 0 && (
                  <p className="font-body text-[#8A8A82] italic text-center py-6">
                    No messages yet. Say hello.
                  </p>
                )}
                {thread.messages.map((m) => {
                  const isAdmin = m.from_role === "admin";
                  return (
                    <div key={m.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-sm p-3 ${
                        isAdmin ? "border border-[#C9A961]/40 bg-[#C9A961]/[0.06]" : "border border-white/10 bg-black/40"
                      }`}>
                        <div className={`font-grotesk text-[9px] uppercase tracking-[0.25em] mb-1 ${
                          isAdmin ? "text-[#C9A961]" : "text-[#8A8A82]"
                        }`}>
                          {isAdmin ? "· Board Secretariat ·" : m.author_name}
                        </div>
                        <p className="font-body text-sm text-[#F5F5F0] whitespace-pre-wrap">{m.body}</p>
                        {m.meeting_at && (
                          <div className="mt-2 rounded-sm border border-[#C9A961]/40 bg-black/40 p-2 flex items-center gap-2">
                            <CalIcon size={13} className="text-[#C9A961]" />
                            <div className="flex-1">
                              <div className="font-body text-xs text-[#F5F5F0]">{m.meeting_at}</div>
                              {m.meeting_link && <div className="font-body text-[10px] text-[#C9A961] truncate">{m.meeting_link}</div>}
                            </div>
                          </div>
                        )}
                        {m.attachment_filename && (
                          <button onClick={() => downloadAttachment(thread.founder.id, m)}
                            className="mt-2 inline-flex items-center gap-1 rounded-sm border border-[#C9A961]/40 px-2 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] hover:bg-[#C9A961] hover:text-black">
                            <Download size={11} /> {m.attachment_filename}
                          </button>
                        )}
                        <div className="font-body text-[10px] text-[#8A8A82] mt-1">{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="space-y-3 pt-3 border-t border-[#C9A961]/10">
                <textarea value={text} onChange={(e) => setText(e.target.value)}
                  data-testid="admin-concierge-input"
                  placeholder="Note to the Founder…" rows={2}
                  className="w-full bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-3 font-body text-sm text-[#F5F5F0] outline-none" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={meetingAt} onChange={(e) => setMeetingAt(e.target.value)}
                    data-testid="admin-concierge-meeting-at"
                    placeholder="Meeting date/time (optional)"
                    className="bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-2 font-body text-xs text-[#F5F5F0] outline-none" />
                  <input type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)}
                    data-testid="admin-concierge-meeting-link"
                    placeholder="Meeting link (optional)"
                    className="bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-2 font-body text-xs text-[#F5F5F0] outline-none" />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:border-[#C9A961]">
                    <Paperclip size={12} /> {file ? file.name.slice(0, 20) : "Attach"}
                    <input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                  <button type="submit" disabled={busy} data-testid="admin-concierge-send"
                    className="ml-auto rounded-sm bg-[#C9A961] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                    <Send size={13} /> Send
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
